// ============================================================================
// TERMOPRINTER — kassa qurilmasiga ulangan printer (har bir qurilmada o'z sozlamasi).
//
//   'usb'     — WebUSB (Chrome/Edge). Windows'da printerga WinUSB drayveri kerak bo'lishi mumkin (Zadig).
//   'serial'  — Web Serial: COM-port / USB-serial printerlar (Chrome/Edge).
//   'browser' — oddiy chop etish oynasi (window.print). Chrome --kiosk-printing bilan oynasiz chiqadi.
//
// Baytlar escpos.ts da tayyorlanadi. Bu fayl faqat ulanish, sozlama va yuborish bilan shug'ullanadi.
// ============================================================================

import { useSyncExternalStore } from 'react';
import { colsFor, concatBytes, hasCash, kitchenBytes, receiptBytes, refundBytes, testBytes } from './escpos';
import type { Codepage, EscOptions, Paper, RefundSlip } from './escpos';
import { renderHeaderBlock } from './bitmapCanvas';
import type { Boot, KassaSettings, LocalOrder, OrderItemJson } from './logic';

// ---------------------------------------------------------------------------
// WebUSB / Web Serial — kerakli qismi (TS standart kutubxonasida yo'q)
// ---------------------------------------------------------------------------
interface UsbEndpoint {
  endpointNumber: number;
  direction: 'in' | 'out';
  type: 'bulk' | 'interrupt' | 'isochronous';
}
interface UsbAlternate {
  alternateSetting: number;
  interfaceClass: number;
  endpoints: UsbEndpoint[];
}
interface UsbInterface {
  interfaceNumber: number;
  alternates: UsbAlternate[];
}
interface UsbConfiguration {
  configurationValue: number;
  interfaces: UsbInterface[];
}
export interface UsbDeviceLike {
  vendorId: number;
  productId: number;
  productName?: string;
  manufacturerName?: string;
  serialNumber?: string;
  opened: boolean;
  configuration: UsbConfiguration | null;
  configurations: UsbConfiguration[];
  open(): Promise<void>;
  close(): Promise<void>;
  selectConfiguration(value: number): Promise<void>;
  claimInterface(n: number): Promise<void>;
  selectAlternateInterface(n: number, alt: number): Promise<void>;
  transferOut(endpoint: number, data: Uint8Array): Promise<{ status: string }>;
}
interface UsbApi {
  requestDevice(o: { filters: object[] }): Promise<UsbDeviceLike>;
  getDevices(): Promise<UsbDeviceLike[]>;
}
export interface SerialPortLike {
  open(o: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  writable: WritableStream<Uint8Array> | null;
  getInfo(): { usbVendorId?: number; usbProductId?: number };
}
interface SerialApi {
  requestPort(o?: object): Promise<SerialPortLike>;
  getPorts(): Promise<SerialPortLike[]>;
}

function usbApi(): UsbApi | null {
  return typeof navigator !== 'undefined' ? ((navigator as unknown as { usb?: UsbApi }).usb ?? null) : null;
}
function serialApi(): SerialApi | null {
  return typeof navigator !== 'undefined' ? ((navigator as unknown as { serial?: SerialApi }).serial ?? null) : null;
}

export function printerSupport(): { usb: boolean; serial: boolean } {
  return { usb: Boolean(usbApi()), serial: Boolean(serialApi()) };
}

// ---------------------------------------------------------------------------
// Sozlama (qurilmada, localStorage)
// ---------------------------------------------------------------------------
export type Transport = 'browser' | 'usb' | 'serial';

export interface PrinterConfig {
  transport: Transport;
  /** Ulangan printerning nomi (ko'rsatish uchun) */
  name: string | null;
  vendorId: number | null;
  productId: number | null;
  serialNumber: string | null;
  baudRate: number;
  paper: Paper;
  codepage: Codepage;
  /** To'lovdan keyin chek o'zi chiqadi */
  autoReceipt: boolean;
  /** Naqd to'lovda pul qutisi ochiladi (printerga ulangan quti) */
  drawer: boolean;
  /** Oshxona cheki (faqat "Oshxona printerlari" moduli yoqilgan restoranda) */
  kitchen: boolean;
  /** Ofitsiantlar so'ragan hisob cheklari shu printerdan chiqadi (Q-067) */
  waiterBills: boolean;
}

export const DEFAULT_PRINTER: PrinterConfig = {
  transport: 'browser',
  name: null,
  vendorId: null,
  productId: null,
  serialNumber: null,
  baudRate: 9600,
  paper: 80,
  codepage: 'ascii',
  autoReceipt: false,
  drawer: false,
  kitchen: false,
  waiterBills: false,
};

const KEY = 'onepos.printer';

function loadConfig(): PrinterConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_PRINTER };
    const v = JSON.parse(raw) as Partial<PrinterConfig>;
    return {
      ...DEFAULT_PRINTER,
      ...v,
      transport: v.transport === 'usb' || v.transport === 'serial' ? v.transport : 'browser',
      paper: v.paper === 58 ? 58 : 80,
      codepage: v.codepage === 'cp866' ? 'cp866' : 'ascii',
    };
  } catch {
    return { ...DEFAULT_PRINTER };
  }
}

function saveConfig(c: PrinterConfig) {
  try {
    localStorage.setItem(KEY, JSON.stringify(c));
  } catch {
    // saqlab bo'lmadi (maxfiy oyna) — sozlama shu ochilishgacha amal qiladi
  }
}

// ---------------------------------------------------------------------------
// Holat (useSyncExternalStore)
// ---------------------------------------------------------------------------
export type PrinterStatus = 'browser' | 'ready' | 'offline' | 'busy';

interface State {
  config: PrinterConfig;
  status: PrinterStatus;
  error: string | null;
}

let state: State | null = null;
const listeners = new Set<() => void>();

function get(): State {
  if (!state) {
    const config = loadConfig();
    state = { config, status: config.transport === 'browser' ? 'browser' : 'offline', error: null };
  }
  return state;
}

function set(patch: Partial<State>) {
  state = { ...get(), ...patch };
  for (const l of listeners) l();
}

export function usePrinter(): State {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    get,
    get,
  );
}

export function printerConfig(): PrinterConfig {
  return get().config;
}

export function updatePrinter(patch: Partial<PrinterConfig>) {
  const config = { ...get().config, ...patch };
  saveConfig(config);
  set({ config });
}

export class PrinterError extends Error {}

// ---------------------------------------------------------------------------
// Ulanish
// ---------------------------------------------------------------------------
let usb: { device: UsbDeviceLike; endpoint: number } | null = null;
let serial: SerialPortLike | null = null;

/** Bulk OUT endpoint'li interfeysni topib egallaydi (printer klassi 7 birinchi) */
async function openUsb(device: UsbDeviceLike): Promise<{ device: UsbDeviceLike; endpoint: number }> {
  if (!device.opened) await device.open();
  if (!device.configuration) await device.selectConfiguration(device.configurations[0]?.configurationValue ?? 1);
  const config = device.configuration ?? device.configurations[0];
  const candidates: { iface: UsbInterface; alt: UsbAlternate; ep: UsbEndpoint }[] = [];
  for (const iface of config?.interfaces ?? []) {
    for (const alt of iface.alternates) {
      const ep = alt.endpoints.find((e) => e.direction === 'out' && e.type === 'bulk');
      if (ep) candidates.push({ iface, alt, ep });
    }
  }
  candidates.sort((a, b) => Number(b.alt.interfaceClass === 7) - Number(a.alt.interfaceClass === 7));
  const pick = candidates[0];
  if (!pick) throw new PrinterError('Bu qurilma printerga o‘xshamaydi (ma’lumot yuboriladigan kanal yo‘q).');
  try {
    await device.claimInterface(pick.iface.interfaceNumber);
    if (pick.alt.alternateSetting) await device.selectAlternateInterface(pick.iface.interfaceNumber, pick.alt.alternateSetting);
  } catch {
    throw new PrinterError(
      'Printerni egallab bo‘lmadi. Uni boshqa dastur yoki Windows drayveri band qilgan bo‘lishi mumkin — printerga WinUSB drayverini o‘rnating yoki "COM-port" usulini sinang.',
    );
  }
  return { device, endpoint: pick.ep.endpointNumber };
}

function sameUsb(d: UsbDeviceLike, c: PrinterConfig): boolean {
  return d.vendorId === c.vendorId && d.productId === c.productId && (c.serialNumber ? d.serialNumber === c.serialNumber : true);
}

function usbName(d: UsbDeviceLike): string {
  return [d.manufacturerName, d.productName].filter(Boolean).join(' ') || `USB ${hex(d.vendorId)}:${hex(d.productId)}`;
}
function hex(n: number): string {
  return n.toString(16).padStart(4, '0');
}

/** USB printerni tanlash (brauzer oynasi) va ulash */
export async function connectUsb(): Promise<void> {
  const api = usbApi();
  if (!api) throw new PrinterError('Bu brauzer USB printerni qo‘llamaydi. Chrome yoki Edge’dan foydalaning.');
  let device: UsbDeviceLike;
  try {
    device = await api.requestDevice({ filters: [] });
  } catch {
    return; // foydalanuvchi oynani yopdi
  }
  set({ status: 'busy', error: null });
  try {
    await closeAll();
    usb = await openUsb(device);
    updatePrinter({
      transport: 'usb',
      name: usbName(device),
      vendorId: device.vendorId,
      productId: device.productId,
      serialNumber: device.serialNumber ?? null,
    });
    set({ status: 'ready' });
  } catch (e) {
    usb = null;
    set({ status: 'offline', error: errorText(e) });
    throw e;
  }
}

/** COM-port (Web Serial) printerni tanlash va ulash */
export async function connectSerial(baudRate: number): Promise<void> {
  const api = serialApi();
  if (!api) throw new PrinterError('Bu brauzer COM-portni qo‘llamaydi. Chrome yoki Edge’dan foydalaning.');
  let port: SerialPortLike;
  try {
    port = await api.requestPort();
  } catch {
    return;
  }
  set({ status: 'busy', error: null });
  try {
    await closeAll();
    await port.open({ baudRate });
    serial = port;
    const info = port.getInfo();
    updatePrinter({
      transport: 'serial',
      name: info.usbVendorId ? `COM-port ${hex(info.usbVendorId)}:${hex(info.usbProductId ?? 0)}` : 'COM-port',
      vendorId: info.usbVendorId ?? null,
      productId: info.usbProductId ?? null,
      serialNumber: null,
      baudRate,
    });
    set({ status: 'ready' });
  } catch {
    serial = null;
    set({ status: 'offline', error: 'COM-portni ochib bo‘lmadi — u boshqa dasturda band emasligini tekshiring.' });
    throw new PrinterError('COM-portni ochib bo‘lmadi — u boshqa dasturda band emasligini tekshiring.');
  }
}

async function closeAll() {
  const u = usb;
  const s = serial;
  usb = null;
  serial = null;
  await u?.device.close().catch(() => undefined);
  await s?.close().catch(() => undefined);
}

/** Printerni uzish: keyin chek oddiy chop etish oynasi orqali chiqadi */
export async function disconnectPrinter(): Promise<void> {
  await closeAll();
  updatePrinter({ transport: 'browser', name: null, vendorId: null, productId: null, serialNumber: null });
  set({ status: 'browser', error: null });
}

/** Avval ruxsat berilgan printerni qayta ulash (sahifa qayta ochilganda, kabel qayta ulanganda) */
export async function reconnectPrinter(): Promise<boolean> {
  const c = get().config;
  if (c.transport === 'browser') return false;
  try {
    if (c.transport === 'usb') {
      if (usb?.device.opened) return true;
      const api = usbApi();
      const device = (await api?.getDevices())?.find((d) => sameUsb(d, c));
      if (!device) throw new PrinterError('Printer topilmadi — kabel va quvvatni tekshiring.');
      usb = await openUsb(device);
    } else {
      if (serial?.writable) return true;
      const api = serialApi();
      const ports = (await api?.getPorts()) ?? [];
      const port = ports.find((p) => !c.vendorId || p.getInfo().usbVendorId === c.vendorId) ?? null;
      if (!port) throw new PrinterError('Printer topilmadi — kabel va quvvatni tekshiring.');
      if (!port.writable) await port.open({ baudRate: c.baudRate });
      serial = port;
    }
    set({ status: 'ready', error: null });
    return true;
  } catch (e) {
    usb = null;
    serial = null;
    set({ status: 'offline', error: errorText(e) });
    return false;
  }
}

function errorText(e: unknown): string {
  return e instanceof PrinterError ? e.message : 'Printer javob bermadi — kabel va quvvatni tekshiring.';
}

const CHUNK = 4096;

async function write(bytes: Uint8Array): Promise<void> {
  if (usb) {
    for (let i = 0; i < bytes.length; i += CHUNK) {
      const r = await usb.device.transferOut(usb.endpoint, bytes.slice(i, i + CHUNK));
      if (r.status !== 'ok') throw new PrinterError('Printer ma’lumotni qabul qilmadi.');
    }
    return;
  }
  if (serial?.writable) {
    const w = serial.writable.getWriter();
    try {
      await w.write(bytes);
    } finally {
      w.releaseLock();
    }
    return;
  }
  throw new PrinterError('Printer ulanmagan.');
}

/** Baytlarni printerga yuborish; aloqa uzilgan bo'lsa bir marta qayta ulanadi */
export async function sendToPrinter(bytes: Uint8Array): Promise<void> {
  if (get().config.transport === 'browser') throw new PrinterError('Printer ulanmagan.');
  if (!usb && !serial && !(await reconnectPrinter())) throw new PrinterError(get().error ?? 'Printer ulanmagan.');
  try {
    await write(bytes);
  } catch {
    await closeAll();
    if (!(await reconnectPrinter())) throw new PrinterError(get().error ?? 'Printer ulanmagan.');
    try {
      await write(bytes);
    } catch (e) {
      set({ status: 'offline', error: errorText(e) });
      throw new PrinterError(errorText(e));
    }
  }
  if (get().status !== 'ready') set({ status: 'ready', error: null });
}

// ---------------------------------------------------------------------------
// Kassa uchun
// ---------------------------------------------------------------------------
function escOptions(c: PrinterConfig): EscOptions {
  return { paper: c.paper, codepage: c.codepage };
}

/**
 * Belgili matnlar ("Tepadagi xabar" — Q-069, "Nomdan keyingi matn" — Q-068): matn va belgi bitta rasmga chiziladi
 * (termoprinter matn yoniga rasm qo'ya olmaydi). Chizib bo'lmasa — oddiy matn chiqadi.
 */
function receiptOptions(
  c: PrinterConfig,
  settings: Parameters<typeof receiptBytes>[2],
): EscOptions & { headerBlock?: ReturnType<typeof renderHeaderBlock>; topBlock?: ReturnType<typeof renderHeaderBlock> } {
  const d = settings.receipt;
  const width = colsFor(c.paper) * 12;
  const block = (icon: Parameters<typeof renderHeaderBlock>[0] | null | undefined, text: string, style: Parameters<typeof renderHeaderBlock>[2], size: number) => {
    if (!icon || !text.trim()) return null;
    try {
      return renderHeaderBlock(icon, text, style, size, width);
    } catch {
      return null;
    }
  };
  if (!d) return escOptions(c);
  return {
    ...escOptions(c),
    headerBlock: block(d.headerIcon, settings.receiptHeader, d.styles.contacts, d.headerIconSize),
    topBlock: block(d.topIcon, d.top, d.styles.top, d.topIconSize),
  };
}

export function hasDevicePrinter(): boolean {
  return get().config.transport !== 'browser';
}

/** Oshxona cheki shu restoranda ruxsat etilganmi (tarif: "Oshxona printerlari" moduli) */
export function kitchenAllowed(boot: Pick<Boot, 'modules' | 'settings'>): boolean {
  return boot.modules.includes('kitchen_printers') && ['printer', 'screen_and_printer'].includes(String(boot.settings['flow.kitchen_routing']));
}

/** Brauzer orqali chop etish (ReceiptPrint sahifada bo'lishi kerak) */
function browserPrint(delay = 60): Promise<void> {
  return new Promise((resolve) =>
    setTimeout(() => {
      window.print();
      resolve();
    }, delay),
  );
}

/** "Chek" tugmasi: printer ulangan bo'lsa — unga, aks holda chop etish oynasi */
export async function printReceipt(order: LocalOrder, boot: Boot, settings: KassaSettings): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser') return browserPrint();
  await sendToPrinter(receiptBytes(order, boot, settings, receiptOptions(c, settings)));
}

/** Hisob cheki (prechek, Q-062): brauzerda — sahifadagi "hisob" qog'ozi chop etiladi */
export async function printBill(order: LocalOrder, boot: Boot, settings: KassaSettings): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser') return browserPrint(150);
  await sendToPrinter(receiptBytes(order, boot, settings, { ...receiptOptions(c, settings), bill: true }));
}

/**
 * Buyurtmadan keyin (to'lov yoki saqlash): sozlamaga qarab chek, oshxona cheki, pul qutisi.
 * Hech narsa kerak bo'lmasa — hech narsa qilmaydi.
 */
export async function printAfterSale(order: LocalOrder, boot: Boot, settings: KassaSettings): Promise<void> {
  const c = get().config;
  const kitchen = c.kitchen && kitchenAllowed(boot);
  const drawer = c.drawer && hasCash(order);
  if (c.transport === 'browser') {
    if (c.autoReceipt) await browserPrint(150);
    return;
  }
  const parts: Uint8Array[] = [];
  if (c.autoReceipt) parts.push(receiptBytes(order, boot, settings, { ...receiptOptions(c, settings), drawer }));
  else if (drawer) parts.push(Uint8Array.of(0x1b, 0x70, 0, 25, 250));
  const live = order.items.filter((i) => i.status !== 'voided');
  if (kitchen && live.length > 0) parts.push(kitchenBytes(order, live, escOptions(c)));
  if (parts.length) await sendToPrinter(concatBytes(parts));
}

/** Mavjud buyurtmaga taom qo'shilganda — faqat yangi taomlar oshxona chekiga */
export async function printKitchenExtra(
  order: Pick<LocalOrder, 'daily_number' | 'local_no' | 'created_at_local' | 'order_type' | 'table_name' | 'staff_name' | 'note'>,
  items: readonly Pick<OrderItemJson, 'name' | 'qty' | 'modifiers' | 'note' | 'components'>[],
  boot: Boot,
): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser' || !c.kitchen || !kitchenAllowed(boot) || items.length === 0) return;
  await sendToPrinter(kitchenBytes(order, items, { ...escOptions(c), extra: true }));
}

/** Oshxonaga xabar: taom bekor qilindi, stol almashdi (Q-060). Printer ulanmagan bo'lsa — jim. */
export async function printKitchenNote(
  order: Pick<LocalOrder, 'daily_number' | 'local_no' | 'created_at_local' | 'order_type' | 'table_name' | 'staff_name' | 'note'>,
  items: readonly Pick<OrderItemJson, 'name' | 'qty' | 'modifiers' | 'note' | 'components'>[],
  boot: Boot,
  title: string,
): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser' || !c.kitchen || !kitchenAllowed(boot) || items.length === 0) return;
  await sendToPrinter(kitchenBytes(order, items, { ...escOptions(c), title }));
}

/** Qaytarish cheki (Q-077): faqat ulangan printerda */
export async function printRefund(slip: RefundSlip): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser') throw new PrinterError('Qaytarish cheki uchun printer ulanmagan.');
  await sendToPrinter(refundBytes(slip, escOptions(c)));
}

export async function printTest(info: { tenant: string; device: string }): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser') throw new PrinterError('Avval printerni ulang.');
  await sendToPrinter(testBytes(escOptions(c), { ...info, at: new Date().toISOString() }));
}


/** Chek sozlamalari sahifasidan namuna chek (Q-065): faqat shu qurilmaga printer ulangan bo'lsa */
export async function printReceiptSample(
  order: Parameters<typeof receiptBytes>[0],
  boot: Parameters<typeof receiptBytes>[1],
  settings: Parameters<typeof receiptBytes>[2],
): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser') throw new PrinterError('Bu qurilmaga printer ulanmagan.');
  await sendToPrinter(receiptBytes(order, boot, settings, receiptOptions(c, settings)));
}

/** Ofitsiant so'ragan hisob cheki (Q-067): kassa printeridan; brauzer orqali chop etishda — yo'q (jim chiqmaydi) */
export async function printWaiterBill(
  order: Parameters<typeof receiptBytes>[0],
  boot: Parameters<typeof receiptBytes>[1],
  settings: Parameters<typeof receiptBytes>[2],
): Promise<void> {
  const c = get().config;
  if (c.transport === 'browser') throw new PrinterError('Printer ulanmagan.');
  await sendToPrinter(receiptBytes(order, boot, settings, { ...receiptOptions(c, settings), bill: true }));
}
